import { type PermissionApi } from '@data-delivery/permission-ui-api';

import {
  Badge,
  Button,
  cn,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  EmptyPlaceholder,
  EmptyPlaceholderDescription,
  EmptyPlaceHolderIcon,
  EmptyPlaceholderTitle,
  Kbd,
  KbdGroup,
  ScrollArea,
  ScrollAreaViewport,
} from '@data-delivery/d3s';
import {
  assignmentsQueries,
  sftpUsersKeys,
  useBusinessGroupsRQ,
  useContentAccessRolesQuery,
  useDirectoryPermissionsQuery,
  useLandingFolderRestrictionsByType,
  useOperatorGroupsQuery,
  useOperatorUsersQuery,
  useProductsRQ,
  useRetentionsQuery,
} from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Boxes,
  Clock,
  FileSearch,
  FolderOpen,
  Hash,
  KeyRound,
  Layers3,
  Shield,
  Sparkles,
  UserCog,
  UserIcon,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { logCustomEvent } from '../utils/custom-rum-events';

type GlobalEntityKey =
  | 'users'
  | 'assignments'
  | 'businessGroups'
  | 'products'
  | 'retentionPolicies'
  | 'landingFolderRestrictions'
  | 'directoryPermissions'
  | 'contentAccessRoles'
  | 'operatorUsers'
  | 'operatorGroups';
interface GlobalResultBase {
  id: string;
  label: string;
  description?: string;
  navigateTo: string;
}
interface BusinessGroupResult extends GlobalResultBase {
  entity: 'businessGroups';
  raw: PermissionApi.BusinessGroupEntity;
}
interface ProductResult extends GlobalResultBase {
  entity: 'products';
  raw: PermissionApi.ProductEntity;
}
interface AssignmentResult extends GlobalResultBase {
  entity: 'assignments';
  raw: PermissionApi.AssignmentEntity | unknown;
}
interface UserResult extends GlobalResultBase {
  entity: 'users';
  raw: PermissionApi.UserEntity;
}
interface RetentionPolicyResult extends GlobalResultBase {
  entity: 'retentionPolicies';
  raw: PermissionApi.RetentionEntity;
}
interface LandingFolderRestrictionResult extends GlobalResultBase {
  entity: 'landingFolderRestrictions';
  raw: PermissionApi.LFREntity;
}
interface DirectoryPermissionResult extends GlobalResultBase {
  entity: 'directoryPermissions';
  raw: PermissionApi.DirectoryPermissionEntity;
}
interface ContentAccessRoleResult extends GlobalResultBase {
  entity: 'contentAccessRoles';
  raw: PermissionApi.ContentAccessRoleEntity;
}
interface OperatorUserResult extends GlobalResultBase {
  entity: 'operatorUsers';
  raw: PermissionApi.OperatorUserEntity;
}
interface OperatorGroupResult extends GlobalResultBase {
  entity: 'operatorGroups';
  raw: PermissionApi.OperatorGroupEntity;
}
type GlobalResult =
  | BusinessGroupResult
  | ProductResult
  | AssignmentResult
  | UserResult
  | RetentionPolicyResult
  | LandingFolderRestrictionResult
  | DirectoryPermissionResult
  | ContentAccessRoleResult
  | OperatorUserResult
  | OperatorGroupResult;
interface EntityGroup {
  key: GlobalEntityKey;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  order: number;
}

const ENTITY_GROUPS: EntityGroup[] = [
  { key: 'users', title: 'Users', icon: UserIcon, order: 1 },
  { key: 'assignments', title: 'Assignments', icon: Layers3, order: 2 },
  { key: 'businessGroups', title: 'Business Groups', icon: Boxes, order: 3 },
  { key: 'products', title: 'Products', icon: FileSearch, order: 4 },
  { key: 'retentionPolicies', title: 'Retention Policies', icon: Clock, order: 5 },
  {
    key: 'landingFolderRestrictions',
    title: 'Landing Folder Restrictions',
    icon: FolderOpen,
    order: 6,
  },
  { key: 'directoryPermissions', title: 'Directory Permissions', icon: Shield, order: 7 },
  { key: 'contentAccessRoles', title: 'Content Access Roles', icon: KeyRound, order: 8 },
  { key: 'operatorUsers', title: 'Operator Users', icon: UserCog, order: 9 },
  { key: 'operatorGroups', title: 'Operator Groups', icon: Users, order: 10 },
];

export const SearchCmdk = ({ className }: React.HTMLAttributes<HTMLDivElement>) => {
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery);
    },
    500,
    [searchQuery],
  );
  const minLength = 2;
  const active = debouncedSearchQuery.length >= minLength;

  const usersSearchQuery = useQuery(sftpUsersKeys.visible());
  const { data: assignmentsData } = useQuery(assignmentsQueries.list());
  const { data: businessGroupsData } = useBusinessGroupsRQ();
  const { data: productsData } = useProductsRQ();
  const { data: retentionsData } = useRetentionsQuery();
  const { data: lfrData } = useLandingFolderRestrictionsByType();
  const { data: directoryPermissionsData } = useDirectoryPermissionsQuery();
  const { data: contentAccessRolesData } = useContentAccessRolesQuery();
  const { data: operatorUsersData } = useOperatorUsersQuery();
  const { data: operatorGroupsData } = useOperatorGroupsQuery();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) {
          setOpen(false);
          return;
        }

        setOpen(true);

        requestAnimationFrame(() => {
          const el = document.querySelector('[cmdk-input]') as HTMLInputElement | undefined;
          el?.focus();
        });
      }
    };

    window.addEventListener('keydown', handler);

    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, [open]);

  const onOpenChange = (o: boolean) => {
    setOpen(o);
    setSearchQuery('');
  };

  const navigate = useNavigate();
  const [selectedEntity, setSelectedEntity] = useState<GlobalEntityKey | undefined>(undefined);

  const entityButtonsRef = useRef<HTMLDivElement>(null);

  const resultsViewportRef = useRef<HTMLDivElement>(null);

  const highlight = (text: string | undefined) => {
    if (!text) {
      return null;
    }

    if (!debouncedSearchQuery) {
      return <span>{text}</span>;
    }

    const i = text.toLowerCase().indexOf(debouncedSearchQuery.toLowerCase());
    if (i === -1) {
      return <span>{text}</span>;
    }

    const before = text.slice(0, i);
    const match = text.slice(i, i + debouncedSearchQuery.length);
    const after = text.slice(i + debouncedSearchQuery.length);
    return (
      <span>
        {before}
        <span className='rounded bg-primary/15 px-0.5 text-primary font-medium'>{match}</span>
        {after}
      </span>
    );
  };

  const aggregated = useMemo(() => {
    const agg: Record<GlobalEntityKey, GlobalResult[]> = {
      users: [],
      assignments: [],
      businessGroups: [],
      products: [],
      retentionPolicies: [],
      landingFolderRestrictions: [],
      directoryPermissions: [],
      contentAccessRoles: [],
      operatorUsers: [],
      operatorGroups: [],
    };
    if (active) {
      const lowered = debouncedSearchQuery.toLowerCase();

      if (usersSearchQuery.data?.length) {
        usersSearchQuery.data
          .filter(u => {
            const loginId = (u.loginId || '').toLowerCase();
            const orgName = (u.orgName || '').toLowerCase();
            return loginId.includes(lowered) || orgName.includes(lowered);
          })
          .slice(0, 30)
          .forEach(u => {
            const detailPath = `/configuration/users/${u.id}/detail`;
            const primaryName = u.orgName || u.loginId || u.id;
            const secondaryParts = [];
            if (u.loginId && u.loginId !== primaryName) {
              secondaryParts.push(u.loginId);
            }

            if (u.orgName && u.loginId && u.orgName !== u.loginId) {
              // Already showing orgName as primary, show loginId in secondary
            } else if (!u.orgName && Array.isArray(u.distributionList) && u.distributionList[0]) {
              secondaryParts.push(u.distributionList[0]);
            }

            agg.users.push({
              id: u.id,
              label: primaryName,
              description: secondaryParts.join(' · '),
              navigateTo: detailPath,
              entity: 'users',
              raw: u,
            });
          });
      }

      if (assignmentsData?.length) {
        assignmentsData
          .filter(a => {
            const name = (a.name || '').toLowerCase();
            const id = (a.id || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered);
          })
          .slice(0, 30)
          .forEach(a => {
            let bgId: string | undefined = (a as { businessGroup?: string }).businessGroup;
            if (!bgId && typeof a.id === 'string') {
              const colonIdx = a.id.indexOf(':');
              if (colonIdx > 0) {
                bgId = a.id.slice(0, colonIdx);
              }
            }

            const detailPath = bgId
              ? `/business-groups/${encodeURIComponent(bgId)}/assignments/${encodeURIComponent(
                  a.id,
                )}`
              : `/business-groups/unknown/assignments/${encodeURIComponent(a.id)}`;

            const bgName = bgId
              ? businessGroupsData?.find(bg => bg.id === bgId)?.name || bgId
              : undefined;
            const secondaryParts = [];
            if (bgName) {
              secondaryParts.push(bgName);
            }

            if (a.id && a.id !== a.name) {
              secondaryParts.push(a.id);
            }

            agg.assignments.push({
              id: a.id,
              label: a.name || a.id,
              description: secondaryParts.join(' · '),
              navigateTo: detailPath,
              entity: 'assignments',
              raw: a,
            });
          });
      }

      if (businessGroupsData?.length) {
        businessGroupsData
          .filter(bg => {
            const name = (bg.name || '').toLowerCase();
            const shortName = (bg.shortName || '').toLowerCase();
            return name.includes(lowered) || shortName.includes(lowered);
          })
          .slice(0, 30)
          .forEach(bg => {
            agg.businessGroups.push({
              id: bg.id,
              label: bg.name || bg.shortName || bg.id,
              description: bg.shortName && bg.shortName !== bg.name ? bg.shortName : '',
              navigateTo: `/configuration/business-groups/detail/${bg.id}`,
              entity: 'businessGroups',
              raw: bg,
            });
          });
      }

      if (productsData?.length) {
        productsData
          .filter(p => {
            const name = (p.name || '').toLowerCase();
            const id = (p.id || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered);
          })
          .slice(0, 30)
          .forEach(p => {
            const bgId = (p as { businessGroup?: string }).businessGroup;
            const bgName = bgId
              ? businessGroupsData?.find(bg => bg.id === bgId)?.name || bgId
              : undefined;
            const secondaryParts = [];
            if (p.id && p.id !== p.name) {
              secondaryParts.push(p.id);
            }

            if (bgName) {
              secondaryParts.push(bgName);
            }

            agg.products.push({
              id: p.id,
              label: p.name || p.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/configuration/products/detail/${p.id}`,
              entity: 'products',
              raw: p,
            });
          });
      }

      if (retentionsData?.length) {
        retentionsData
          .filter(r => {
            const name = (r.name || '').toLowerCase();
            const id = (r.id || '').toLowerCase();
            const path = (r.path || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered) || path.includes(lowered);
          })
          .slice(0, 30)
          .forEach(r => {
            const secondaryParts = [];
            if (r.path) {
              secondaryParts.push(r.path);
            }

            if (r.id && r.id !== r.name) {
              secondaryParts.push(r.id);
            }

            agg.retentionPolicies.push({
              id: r.id,
              label: r.name || r.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/retentions/edit/${encodeURIComponent(r.id)}`,
              entity: 'retentionPolicies',
              raw: r,
            });
          });
      }

      if (lfrData?.length) {
        lfrData
          .filter(lfr => {
            const name = (lfr.name || '').toLowerCase();
            const id = (lfr.id || '').toLowerCase();
            const folder = (lfr.folder || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered) || folder.includes(lowered);
          })
          .slice(0, 30)
          .forEach(lfr => {
            const secondaryParts = [];
            if (lfr.folder) {
              secondaryParts.push(lfr.folder);
            }

            if (lfr.id && lfr.id !== lfr.name) {
              secondaryParts.push(lfr.id);
            }

            agg.landingFolderRestrictions.push({
              id: lfr.id,
              label: lfr.name || lfr.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/configuration/landing-folder-restrictions/edit/${encodeURIComponent(
                lfr.id,
              )}`,
              entity: 'landingFolderRestrictions',
              raw: lfr,
            });
          });
      }

      if (directoryPermissionsData?.length) {
        directoryPermissionsData
          .filter(dp => {
            const name = (dp.name || '').toLowerCase();
            const id = (dp.id || '').toLowerCase();
            const paths = (dp.relativePaths ?? []).join(' ').toLowerCase();
            return name.includes(lowered) || id.includes(lowered) || paths.includes(lowered);
          })
          .slice(0, 30)
          .forEach(dp => {
            const secondaryParts = [];
            if (dp.relativePaths?.length) {
              secondaryParts.push(dp.relativePaths[0]);
            }

            if (dp.id && dp.id !== dp.name) {
              secondaryParts.push(dp.id);
            }

            agg.directoryPermissions.push({
              id: dp.id,
              label: dp.name || dp.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/directory-permissions/edit/${encodeURIComponent(dp.id)}`,
              entity: 'directoryPermissions',
              raw: dp,
            });
          });
      }

      if (contentAccessRolesData?.length) {
        contentAccessRolesData
          .filter(car => {
            const name = (car.name || '').toLowerCase();
            const id = (car.id || '').toLowerCase();
            const description = (car.description || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered) || description.includes(lowered);
          })
          .slice(0, 30)
          .forEach(car => {
            const secondaryParts = [];
            if (car.description) {
              secondaryParts.push(car.description);
            }

            if (car.id && car.id !== car.name) {
              secondaryParts.push(car.id);
            }

            agg.contentAccessRoles.push({
              id: car.id,
              label: car.name || car.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/content-access-roles/edit/${encodeURIComponent(car.id)}`,
              entity: 'contentAccessRoles',
              raw: car,
            });
          });
      }

      if (operatorUsersData?.length) {
        operatorUsersData
          .filter(ou => {
            const name = (ou.name || '').toLowerCase();
            const id = (ou.id || '').toLowerCase();
            const email = (ou.email || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered) || email.includes(lowered);
          })
          .slice(0, 30)
          .forEach(ou => {
            const secondaryParts = [];
            if (ou.email) {
              secondaryParts.push(ou.email);
            }

            if (ou.id && ou.id !== ou.name) {
              secondaryParts.push(ou.id);
            }

            agg.operatorUsers.push({
              id: ou.id,
              label: ou.name || ou.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/operators/users/edit/${encodeURIComponent(ou.id)}`,
              entity: 'operatorUsers',
              raw: ou,
            });
          });
      }

      if (operatorGroupsData?.length) {
        operatorGroupsData
          .filter(og => {
            const name = (og.name || '').toLowerCase();
            const id = (og.id || '').toLowerCase();
            return name.includes(lowered) || id.includes(lowered);
          })
          .slice(0, 30)
          .forEach(og => {
            const secondaryParts = [];
            if (og.id && og.id !== og.name) {
              secondaryParts.push(og.id);
            }

            agg.operatorGroups.push({
              id: og.id,
              label: og.name || og.id,
              description: secondaryParts.join(' · '),
              navigateTo: `/operators/groups/edit/${encodeURIComponent(og.id)}`,
              entity: 'operatorGroups',
              raw: og,
            });
          });
      }
    }

    return agg;
  }, [
    active,
    debouncedSearchQuery,
    usersSearchQuery.data,
    assignmentsData,
    businessGroupsData,
    productsData,
    retentionsData,
    lfrData,
    directoryPermissionsData,
    contentAccessRolesData,
    operatorUsersData,
    operatorGroupsData,
  ]);

  useEffect(() => {
    if (open) {
      const stored = sessionStorage.getItem('globalSearch.selectedEntity') as
        | GlobalEntityKey
        | undefined;
      if (stored) {
        setSelectedEntity(stored);
      }
    } else {
      setSelectedEntity(undefined);
    }
  }, [open]);
  useEffect(() => {
    if (selectedEntity) {
      sessionStorage.setItem('globalSearch.selectedEntity', selectedEntity);
    }
  }, [selectedEntity]);

  useEffect(() => {
    if (!entityButtonsRef.current || !selectedEntity) {
      return;
    }

    const btn = entityButtonsRef.current.querySelector(`button[data-entity="${selectedEntity}"]`);
    if (btn) {
      btn.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedEntity]);

  const hasAny = Object.values(aggregated).some(arr => arr.length > 0);
  const totalCount = Object.values(aggregated).reduce((acc, arr) => acc + arr.length, 0);

  useEffect(() => {
    if (!open) {
      return;
    }

    const available = ENTITY_GROUPS.filter(g => aggregated[g.key].length > 0)
      .sort((a, b) => a.order - b.order)
      .map(g => g.key);

    if (available.length === 0) {
      if (selectedEntity !== undefined) {
        setSelectedEntity(undefined);
      }

      return;
    }

    if (!selectedEntity || aggregated[selectedEntity].length === 0) {
      const orderedAll = [...ENTITY_GROUPS].sort((a, b) => a.order - b.order).map(g => g.key);
      const prevIndex = selectedEntity ? orderedAll.indexOf(selectedEntity) : -1;

      let choice = available.find(key => orderedAll.indexOf(key) > prevIndex);
      if (!choice) {
        const reversed = [...available].reverse();
        choice = reversed.find(key => orderedAll.indexOf(key) < prevIndex);
      }

      choice ||= available[0];

      setSelectedEntity(choice);
    }
  }, [open, aggregated, selectedEntity]);

  return (
    <div className={className}>
      <SearchCmdkButton
        onBtnClick={() => {
          logCustomEvent('search.users.dialog.open');
          setOpen(true);
        }}
      />
      <CommandDialog
        open={open}
        onOpenChange={onOpenChange}
        className='max-w-4xl w-[960px] sm:w-[90vw] top-20 translate-y-0'
      >
        <div className='flex h-full w-full flex-col'>
          <div
            className='relative flex flex-col border-b border-transparent bg-transparent'
            onClick={() => {
              const el = document.querySelector('[cmdk-input]') as HTMLInputElement | undefined;
              el?.focus();
            }}
          >
            <CommandInput
              className='[&_[cmdk-input-wrapper]_svg]:hidden px-3'
              placeholder='Search users, assignments, business groups, products, retention policies, landing folder restrictions, directory permissions, content access roles, operator users, operator groups...'
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            {debouncedSearchQuery && active && (
              <div className='absolute right-10 top-1/2 -translate-y-1/2 flex items-center'>
                <Badge variant='outline' className='rounded-sm px-1 py-0 text-[10px] font-normal'>
                  {debouncedSearchQuery.length} chars
                </Badge>
              </div>
            )}
          </div>
          <CommandList disableScrollArea>
            {!active && (
              <CommandEmpty className='p-5'>
                <EmptyPlaceholder className=''>
                  <EmptyPlaceHolderIcon name='Reader' />
                  <EmptyPlaceholderTitle title='Global Search' />
                  <EmptyPlaceholderDescription title='Start typing to find Users, Assignments, Business Groups, Products, Retention Policies, Landing Folder Restrictions, Directory Permissions, Content Access Roles, Operator Users and Operator Groups across the app.' />
                </EmptyPlaceholder>
              </CommandEmpty>
            )}
            {active && !hasAny && usersSearchQuery && (
              <CommandEmpty>
                <EmptyPlaceholder>
                  <EmptyPlaceHolderIcon name='Text' />
                  <EmptyPlaceholderTitle title='No matches found' />
                  <EmptyPlaceholderDescription title='Try refining your query or using different keywords.' />
                </EmptyPlaceholder>
              </CommandEmpty>
            )}
            {active && hasAny && (
              <div className='flex w-full gap-2 pr-2 min-h-0 h-[400px] items-stretch'>
                {/* Left entity selector (simple native scroll) */}
                <div
                  className='w-52 shrink-0 h-full border-r pr-2 overflow-y-auto'
                  ref={entityButtonsRef}
                  role='tablist'
                  aria-label='Search entity types'
                >
                  <span className='px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
                    Entities
                  </span>
                  <div className='flex flex-col gap-1 pb-2'>
                    {ENTITY_GROUPS.filter(g => aggregated[g.key].length > 0)
                      .sort((a, b) => a.order - b.order)
                      .map(group => {
                        const activeEntity = selectedEntity === group.key;
                        return (
                          <button
                            key={group.key}
                            type='button'
                            role='tab'
                            aria-selected={activeEntity}
                            tabIndex={activeEntity ? 0 : -1}
                            onClick={() => {
                              setSelectedEntity(group.key);
                            }}
                            onKeyDown={e => {
                              if (
                                !['ArrowUp', 'ArrowDown', 'Home', 'End', 'Enter', ' '].includes(
                                  e.key,
                                )
                              ) {
                                return;
                              }

                              const enabled = ENTITY_GROUPS.filter(
                                g => aggregated[g.key].length > 0,
                              )
                                .sort((a, b) => a.order - b.order)
                                .map(g => g.key);
                              if (!enabled.length) {
                                return;
                              }

                              const currentIndex = enabled.indexOf(group.key);
                              let nextIndex = currentIndex;
                              if (e.key === 'ArrowDown') {
                                nextIndex = (currentIndex + 1) % enabled.length;
                              } else if (e.key === 'ArrowUp') {
                                nextIndex = (currentIndex - 1 + enabled.length) % enabled.length;
                              } else if (e.key === 'Home') {
                                nextIndex = 0;
                              } else if (e.key === 'End') {
                                nextIndex = enabled.length - 1;
                              } else if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedEntity(group.key);
                                e.preventDefault();
                                return;
                              }

                              if (nextIndex !== currentIndex) {
                                e.preventDefault();
                                const nextKey = enabled[nextIndex];
                                setSelectedEntity(nextKey);

                                requestAnimationFrame(() => {
                                  const btn = entityButtonsRef.current?.querySelector(
                                    `button[data-entity="${nextKey}"]`,
                                  ) as HTMLButtonElement | undefined;
                                  btn?.focus();
                                });
                              }
                            }}
                            data-entity={group.key}
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
                              activeEntity ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
                            )}
                          >
                            <span className='inline-flex h-5 w-5 items-center justify-center rounded-sm border bg-muted/40 text-[10px] text-muted-foreground'>
                              {group.key === 'users' && <UserIcon className='h-3.5 w-3.5' />}
                              {group.key === 'assignments' && <Layers3 className='h-3.5 w-3.5' />}
                              {group.key === 'businessGroups' && <Boxes className='h-3.5 w-3.5' />}
                              {group.key === 'products' && <Hash className='h-3.5 w-3.5' />}
                              {group.key === 'retentionPolicies' && (
                                <Clock className='h-3.5 w-3.5' />
                              )}
                              {group.key === 'landingFolderRestrictions' && (
                                <FolderOpen className='h-3.5 w-3.5' />
                              )}
                              {group.key === 'directoryPermissions' && (
                                <Shield className='h-3.5 w-3.5' />
                              )}
                              {group.key === 'contentAccessRoles' && (
                                <KeyRound className='h-3.5 w-3.5' />
                              )}
                              {group.key === 'operatorUsers' && <UserCog className='h-3.5 w-3.5' />}
                              {group.key === 'operatorGroups' && <Users className='h-3.5 w-3.5' />}
                            </span>
                            <span className='flex-1 truncate'>{group.title}</span>
                            <Badge
                              variant='secondary'
                              className='ml-auto rounded-sm px-1 py-0 text-[10px] font-normal'
                            >
                              {aggregated[group.key].length}
                            </Badge>
                          </button>
                        );
                      })}
                  </div>
                </div>
                {/* Right results pane */}
                <ScrollArea className='flex min-w-0 flex-1 flex-col h-full'>
                  <ScrollAreaViewport ref={resultsViewportRef} className='w-full h-full'>
                    {(() => {
                      if (!selectedEntity) {
                        const first = ENTITY_GROUPS.filter(g => aggregated[g.key].length > 0)
                          .sort((a, b) => a.order - b.order)
                          .map(g => g.key)[0];
                        if (first) {
                          setTimeout(() => {
                            setSelectedEntity(first);
                          }, 0);
                        }
                      }

                      if (!selectedEntity) {
                        return null;
                      }

                      const group = ENTITY_GROUPS.find(g => g.key === selectedEntity)!;
                      const results = aggregated[selectedEntity];
                      return (
                        <CommandGroup
                          heading={
                            <span className='flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground'>
                              <group.icon className='h-3.5 w-3.5' /> {group.title}
                              <Badge
                                variant='secondary'
                                className='ml-1 rounded-sm px-1 py-0 text-[10px] font-normal'
                              >
                                {results.length}
                              </Badge>
                            </span>
                          }
                        >
                          {results.map((r, idx) => (
                            <CommandItem
                              key={`${selectedEntity}-${r.id}-${idx}`}
                              value={`${selectedEntity}-${r.id}-${idx}`}
                              onSelect={() => {
                                navigate(r.navigateTo);
                                setOpen(false);
                                logCustomEvent('global.search.navigate', {
                                  entity: selectedEntity,
                                  id: r.id,
                                  query: debouncedSearchQuery,
                                });
                              }}
                              className='group relative cursor-pointer rounded-md px-3 py-2 transition-colors data-[selected=true]:bg-accent'
                            >
                              <div className='flex w-full items-start gap-3'>
                                <span className='mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-md border bg-muted/40 text-[11px] font-medium text-muted-foreground'>
                                  {selectedEntity === 'users' && (
                                    <UserIcon className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'assignments' && (
                                    <Layers3 className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'businessGroups' && (
                                    <Boxes className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'products' && (
                                    <Hash className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'retentionPolicies' && (
                                    <Clock className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'landingFolderRestrictions' && (
                                    <FolderOpen className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'directoryPermissions' && (
                                    <Shield className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'contentAccessRoles' && (
                                    <KeyRound className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'operatorUsers' && (
                                    <UserCog className='h-3.5 w-3.5' />
                                  )}
                                  {selectedEntity === 'operatorGroups' && (
                                    <Users className='h-3.5 w-3.5' />
                                  )}
                                </span>
                                <div className='flex flex-col truncate'>
                                  <span className='truncate font-medium leading-tight'>
                                    {highlight(r.label)}
                                  </span>
                                  {r.description && (
                                    <span className='truncate text-[11px] text-muted-foreground'>
                                      {highlight(r.description)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                          {results.length === 0 && (
                            <div className='px-3 py-4 text-xs text-muted-foreground'>
                              No results in this entity.
                            </div>
                          )}
                        </CommandGroup>
                      );
                    })()}
                  </ScrollAreaViewport>
                </ScrollArea>
              </div>
            )}
          </CommandList>
          <div className='mt-auto border-t px-3 py-2 text-[10px] text-muted-foreground'>
            <div className='flex flex-wrap items-center gap-3'>
              <span className='flex items-center gap-1'>
                <Sparkles className='h-3 w-3' /> Substring match
              </span>
              {active && (
                <Badge variant='secondary' className='rounded-sm px-1 py-0'>
                  {totalCount} total
                </Badge>
              )}
              <div className='hidden items-center gap-3 sm:flex ml-auto'>
                <KbdGroup className='gap-1'>
                  <Kbd>Enter</Kbd>
                  <span className='text-[10px] font-normal'>open</span>
                </KbdGroup>
                <KbdGroup className='gap-1'>
                  <Kbd>Esc</Kbd>
                  <span className='text-[10px] font-normal'>close</span>
                </KbdGroup>
                <KbdGroup className='gap-1'>
                  <Kbd>
                    <ArrowUp className='h-3 w-3' />
                  </Kbd>
                  <Kbd>
                    <ArrowDown className='h-3 w-3' />
                  </Kbd>
                  <span className='text-[10px] font-normal'>navigate</span>
                </KbdGroup>
              </div>
            </div>
          </div>
        </div>
      </CommandDialog>
    </div>
  );
};

const SearchCmdkButton = ({
  className,
  onBtnClick,
}: {
  onBtnClick?: VoidFunction;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => (
  <Button
    variant='outline'
    className={cn(
      'relative h-8 w-full justify-start rounded-[0.5rem] bg-background text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-40 lg:w-64',
      className,
    )}
    onClick={() => {
      onBtnClick?.();
    }}
  >
    <span className='hidden lg:inline-flex'>Search...</span>
    <span className='inline-flex lg:hidden'>Search...</span>
    <KbdGroup className='pointer-events-none absolute right-1.5 top-1.5 hidden sm:inline-flex'>
      <Kbd>⌘</Kbd>
      <Kbd>K</Kbd>
    </KbdGroup>
  </Button>
);
